import { useState } from "react";
import Modal from "components/layout/modal";
import { ExportField, exportToCSV, exportToExcel } from "utils/export-report";

interface ExportModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    allFields: ExportField[];
    data: (Record<string, any> | null)[];
    filename: string;
}

const ExportModal = ({
    open,
    setOpen,
    allFields,
    data,
    filename,
}: ExportModalProps) => {
    const [selected, setSelected] = useState<Set<string>>(
        new Set(allFields.map((f) => f.key)),
    );

    const toggle = (key: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const toggleAll = () => {
        setSelected(
            selected.size === allFields.length
                ? new Set()
                : new Set(allFields.map((f) => f.key)),
        );
    };

    const selectedFields = allFields.filter((f) => selected.has(f.key));

    const handleExport = (format: "csv" | "excel") => {
        if (selectedFields.length === 0) return;
        if (format === "csv") {
            exportToCSV(data, selectedFields, filename);
        } else {
            exportToExcel(data, selectedFields, filename);
        }
        setOpen(false);
    };

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title="Export Report"
            description="Select the fields you want to include in the export.">
            <div className="space-y-4">
                <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">
                        Fields
                    </span>
                    <button
                        onClick={toggleAll}
                        className="text-xs text-green-600 hover:underline">
                        {selected.size === allFields.length
                            ? "Deselect all"
                            : "Select all"}
                    </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {allFields.map((field) => (
                        <label
                            key={field.key}
                            className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={selected.has(field.key)}
                                onChange={() => toggle(field.key)}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            {field.label}
                        </label>
                    ))}
                </div>
                <div className="flex gap-3 pt-2">
                    <button
                        onClick={() => handleExport("csv")}
                        disabled={selectedFields.length === 0}
                        className="flex-1 px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                        Download CSV
                    </button>
                    <button
                        onClick={() => handleExport("excel")}
                        disabled={selectedFields.length === 0}
                        className="flex-1 px-4 py-2 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        Download Excel
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ExportModal;
